import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import Since from '@sorare/core/src/contexts/intl/Since';
import { isType } from '@sorare/core/src/gql';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';

import ItemUser from 'components/ItemPreview/ItemUser';
import CardProperties from 'components/card';
import CardDescription from 'components/token/CardDescription';
import FlexCard from 'components/token/FlexCard';

import { CommonCardPreview_card } from './__generated__/index.graphql';

const MobileCommonCardContainer = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: min-content 1fr;
  width: 100%;
  align-items: start;
  gap: var(--unit);
`;

const MobileCommonCardImgContainer = styled.div`
  width: var(--card-mobile-width);
`;
const MobileCardData = styled(Vertical).attrs({ gap: 0.5 })`
  flex-grow: 1;
  overflow: hidden;
`;

const CardFrame = styled(Vertical).attrs({ gap: 1.5 })`
  position: relative;
  align-items: flex-start;
  border-radius: var(--unit);
`;

const ActionWrapper = styled.div`
  position: absolute;
  right: var(--unit);
  top: var(--unit);
  display: none;
  ${CardFrame}:hover & {
    display: block;
  }
`;

type Props = {
  card: CommonCardPreview_card;
  hideDetails?: boolean;
  action?: ReactNode;
};
const CommonCardPreview = ({ card, hideDetails, action }: Props) => {
  const { up: isTablet } = useScreenSize('tablet');

  const {
    flags: { useTwoColumnsMarketOnMobile = false },
  } = useFeatureFlags();

  if (!useTwoColumnsMarketOnMobile && !isTablet && !hideDetails) {
    return (
      <MobileCommonCardContainer>
        <MobileCommonCardImgContainer>
          <FlexCard withLink card={card} />
        </MobileCommonCardImgContainer>
        <MobileCardData>
          <SBHorizontal gap={0}>
            <CardProperties card={card} withTransferMalus={false} />
            {isType(card, 'Card') && card.ownerSince && (
              <Since date={card.ownerSince} />
            )}
          </SBHorizontal>
          <CardDescription
            card={card}
            Details={LabelM}
            detailsColor="var(--c-neutral-600)"
          />
          {card.user && <ItemUser item={card.user} />}
        </MobileCardData>
      </MobileCommonCardContainer>
    );
  }
  return (
    <CardFrame>
      <FlexCard withLink card={card} />
      {action && <ActionWrapper>{action}</ActionWrapper>}
      {!hideDetails && (
        <Vertical>
          <CardProperties card={card} withTransferMalus={false} />
          {isType(card, 'Card') && card.ownerSince && (
            <Since date={card.ownerSince} />
          )}
        </Vertical>
      )}
    </CardFrame>
  );
};

CommonCardPreview.fragments = {
  card: gql`
    fragment CommonCardPreview_card on AnyCardInterface {
      slug
      ... on Card {
        slug
        ownerSince
      }
      user {
        slug
        ...ItemUser_user
      }
      ...CardDescription_anyCard
      ...CardProperties_anyCard
      ...FlexCard_anyCard
    }
    ${ItemUser.fragments.user}
    ${FlexCard.fragments.anyCard}
    ${CardProperties.fragments.anyCard}
    ${CardDescription.fragments.anyCard}
  ` as TypedDocumentNode<CommonCardPreview_card>,
};
export default CommonCardPreview;
